import { cn } from "@playbrain/ui/cn"
import { usePlaydataReveal } from "@playdata/use-playdata-reveal"
import { Link } from "@tanstack/react-router"
import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

/**
 * A full-width band. Tones match the live site's section themes: white,
 * the #F5F5F5 "light" band, the bright-blue promo band, and the dark hero.
 */
export function PlaydataSection({
  children,
  className,
  id,
  tone = "white",
}: {
  children: ReactNode
  className?: string
  id?: string
  tone?: "white" | "light" | "blue" | "dark" | "wave"
}) {
  const toneClass = {
    blue: "bg-[var(--color-playdata-blue)] text-play-white",
    dark: "playdata-hero text-play-white",
    light: "playdata-band-light text-play-black",
    wave: "playdata-band-wave text-play-black",
    white: "bg-play-white text-play-black",
  }[tone]

  const revealRef = usePlaydataReveal<HTMLDivElement>()

  return (
    <section
      className={cn("px-[2.4rem] py-[6.4rem] md:px-[4rem]", toneClass, className)}
      id={id}
    >
      <div
        className="playdata-reveal mx-auto w-full max-w-[1200px]"
        ref={revealRef}
      >
        {children}
      </div>
    </section>
  )
}

/**
 * Section heading in the live site's measure: Roboto Condensed, weight 400,
 * 31px, centred, under a green line icon. `as="h1"` for page titles.
 */
export function PlaydataHeading({
  as: Tag = "h2",
  children,
  className,
  icon: Icon,
  size,
}: {
  as?: "h1" | "h2" | "h3"
  children: ReactNode
  className?: string
  icon?: LucideIcon
  size?: "page" | "section" | "sub"
}) {
  const resolved = size ?? (Tag === "h1" ? "page" : Tag === "h3" ? "sub" : "section")
  const sizeClass = {
    page: "text-[2.8rem] md:text-[3.4rem]",
    section: "text-[2.6rem] md:text-[3.1rem]",
    sub: "text-[2rem] md:text-[2.2rem]",
  }[resolved]

  return (
    <div className={cn("flex flex-col items-center gap-[1.6rem] text-center", className)}>
      {Icon ? (
        <Icon
          aria-hidden="true"
          className="text-play-green"
          size={44}
          strokeWidth={1.6}
        />
      ) : null}
      <Tag className={sizeClass}>{children}</Tag>
    </div>
  )
}

/** Body copy at the live site's 18.76px / 1.6. */
export function PlaydataLead({
  centered = false,
  children,
  className,
}: {
  centered?: boolean
  children: ReactNode
  className?: string
}) {
  return (
    <p
      className={cn(
        "max-w-[64ch] text-[1.6rem] leading-[1.6] md:text-[1.8rem]",
        centered && "mx-auto text-center",
        className
      )}
    >
      {children}
    </p>
  )
}

type Variant = "primary" | "outline" | "hero"

/*
 * Measured off the live buttons: Noto Sans 18.76px, 2px border, 5.2px radius,
 * ~43px horizontal padding. Primary is magenta with a white border; outline is
 * white with a magenta border and text; the hero's is white with a black border.
 */
const base =
  "inline-flex items-center justify-center rounded-[0.52rem] border-2 px-[4.3rem] py-[1.1rem] text-center text-[1.7rem] leading-[1.2] transition-[filter,background-color] duration-150 hover:brightness-95"
const variants: Record<Variant, string> = {
  hero: "border-play-black bg-play-white text-play-black",
  outline: "border-play-pink bg-play-white text-play-pink",
  primary: "border-play-white bg-play-pink text-play-white",
}

/** The button look, for a real <button> — a form submit is not an anchor. */
export function playdataButtonClassName(variant: Variant = "primary", className?: string) {
  return cn(base, variants[variant], className)
}

export function PlaydataButtonLink({
  children,
  className,
  params,
  to,
  variant = "primary",
}: {
  children: ReactNode
  className?: string
  params: { locale: string }
  to: string
  variant?: Variant
}) {
  return (
    <Link
      className={cn(base, variants[variant], className)}
      params={params}
      to={to}
    >
      {children}
    </Link>
  )
}

export function PlaydataButtonAnchor({
  children,
  className,
  href,
  variant = "primary",
  ...rest
}: {
  children: ReactNode
  className?: string
  href: string
  rel?: string
  target?: string
  variant?: Variant
}) {
  return (
    <a
      className={cn(base, variants[variant], className)}
      href={href}
      {...rest}
    >
      {children}
    </a>
  )
}

/** An image with the live site's 2px green frame. */
export function PlaydataFigure({
  alt,
  className,
  height,
  src,
  width,
}: {
  alt: string
  className?: string
  height: number
  src: string
  width: number
}) {
  return (
    <img
      alt={alt}
      className={cn("playdata-figure h-auto w-full bg-play-white", className)}
      height={height}
      loading="lazy"
      src={src}
      width={width}
    />
  )
}
